import React, { useRef, useState, useCallback, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "../lib/utils";


/* ------------------------------------------------------------------ *
 * useMediaQuery
 *
 * Some layout decisions genuinely cannot be made in CSS — whether a card
 * starts collapsed, whether a pinned horizontal deck mounts at all. Those
 * need the breakpoint in JS.
 *
 * Returns false on the first render so server/prerender output matches a
 * phone, which is the safer default: mobile gets the collapsed, cheaper
 * layout even if the effect never runs.
 * ------------------------------------------------------------------ */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mq.matches);

    // addListener is the Safari < 14 spelling; still worth keeping.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [query]);

  return matches;
}

/* ------------------------------------------------------------------ *
 * Reveal — scroll-in animation.
 * `once` is deliberate: replaying on every scroll-back is a novelty that
 * turns into motion sickness on a long page.
 * ------------------------------------------------------------------ */
export function Reveal({
  children,
  delay = 0,
  y = 22,
  className,
  as: Tag = "div",
}) {
  const reduce = useReducedMotion();
  const MotionTag = motion[Tag] || motion.div;

  if (reduce) return <Tag className={className}>{children}</Tag>;

  return (
    <MotionTag
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px -12% 0px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </MotionTag>
  );
}

/* ------------------------------------------------------------------ *
 * Section — consistent rhythm, heading hierarchy and anchor behaviour.
 * Every section is a <section> with a real <h2>, which is what both
 * screen readers and answer engines walk.
 * ------------------------------------------------------------------ */
export function Section({
  id,
  index,
  eyebrow,
  title,
  lead,
  children,
  className,
  headerClassName,
  wide = false,
}) {
  return (
    <section
      id={id}
      className={cn(
        "relative scroll-mt-24 py-20 sm:py-28 lg:py-36",
        className
      )}
    >
      <div className={cn("shell", wide && "max-w-none px-0 sm:px-0")}>
        {(eyebrow || title) && (
          <div className={cn(wide && "shell", "mb-12 sm:mb-16", headerClassName)}>
            <Reveal>
              <div className="flex items-center gap-4">
                {index && (
                  <span className="font-mono text-[0.65rem] tracking-label text-slate-600">
                    {index}
                  </span>
                )}
                {eyebrow && <span className="eyebrow">{eyebrow}</span>}
                <span className="rule hidden flex-1 sm:block" />
              </div>
            </Reveal>

            {title && (
              <Reveal delay={0.06}>
                <h2 className="mt-5 max-w-4xl text-fluid-2xl font-semibold leading-[1.08] tracking-tight">
                  {title}
                </h2>
              </Reveal>
            )}

            {lead && (
              <Reveal delay={0.12}>
                <p className="mt-5 max-w-prose2 text-fluid-base leading-relaxed text-slate-400">
                  {lead}
                </p>
              </Reveal>
            )}
          </div>
        )}

        {children}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * TiltCard — pointer-driven 3D tilt.
 *
 * Uses raw CSS transforms via a ref rather than React state, so a
 * mousemove doesn't re-render the card's subtree 60 times a second.
 * Disabled on coarse pointers, where it would only fight with scrolling.
 * ------------------------------------------------------------------ */
export function TiltCard({
  children,
  className,
  intensity = 8,
  glare = true,
  scale = 1.012,
}) {
  const outer = useRef(null);
  const inner = useRef(null);
  const glareRef = useRef(null);
  const [active, setActive] = useState(false);
  const reduce = useReducedMotion();

  const onMove = useCallback(
    (e) => {
      if (reduce || !inner.current || !outer.current) return;
      if (window.matchMedia?.("(pointer: coarse)").matches) return;

      const r = outer.current.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width; // 0 → 1
      const py = (e.clientY - r.top) / r.height;

      const rx = (0.5 - py) * intensity * 2;
      const ry = (px - 0.5) * intensity * 2;

      inner.current.style.transform =
        `rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg) scale(${scale})`;

      if (glareRef.current) {
        glareRef.current.style.background =
          `radial-gradient(circle at ${(px * 100).toFixed(1)}% ${(py * 100).toFixed(1)}%,` +
          `rgba(255,255,255,0.14), transparent 55%)`;
      }
    },
    [intensity, scale, reduce]
  );

  const onLeave = useCallback(() => {
    setActive(false);
    if (inner.current) {
      inner.current.style.transform =
        "rotateX(0deg) rotateY(0deg) scale(1)";
    }
  }, []);

  return (
    <div
      ref={outer}
      className={cn("tilt-scene", className)}
      onMouseMove={onMove}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={onLeave}
    >
      <div
        ref={inner}
        className="tilt-layer h-full transition-transform duration-500 ease-out2"
      >
        <div className="relative h-full">
          {children}
          {glare && (
            <div
              ref={glareRef}
              aria-hidden="true"
              className={cn(
                "pointer-events-none absolute inset-0 rounded-[inherit] transition-opacity duration-500",
                active ? "opacity-100" : "opacity-0"
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Marquee — infinite horizontal scroller.
 * The children are rendered twice and translated -50%, which is the only
 * way to loop seamlessly without measuring anything at runtime.
 * ------------------------------------------------------------------ */
export function Marquee({ items, speed = "normal", reverse = false, className }) {
  const doubled = [...items, ...items];
  return (
    <div className={cn("mask-fade-x relative overflow-hidden", className)}>
      <div
        className={cn(
          "flex w-max gap-3",
          speed === "slow" ? "animate-marquee-slow" : "animate-marquee",
          reverse && "[animation-direction:reverse]"
        )}
        style={{ willChange: "transform" }}
      >
        {doubled.map((item, i) => (
          <span
            key={i}
            aria-hidden={i >= items.length}
            className="chip whitespace-nowrap border-white/[0.07] bg-white/[0.02] py-1.5"
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * StatBlock — a number with the claim that backs it.
 * ------------------------------------------------------------------ */
export function StatBlock({ value, unit, label, detail, className }) {
  return (
    <div className={cn("group relative", className)}>
      <div className="flex items-baseline gap-1.5">
        <span className="gradient-text font-display text-fluid-xl font-semibold leading-none">
          {value}
        </span>
        {unit && (
          <span className="font-mono text-[0.68rem] uppercase tracking-wider2 text-slate-500">
            {unit}
          </span>
        )}
      </div>
      <p className="mt-2 text-sm font-medium leading-snug text-slate-200">
        {label}
      </p>
      {detail && (
        <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{detail}</p>
      )}
    </div>
  );
}
